/**
 * SessionPage — Realtime voice roleplay session screen.
 *
 * Route: /app/session/:sessionId
 *
 * ## EPIC-01-04 implementation (this file)
 * - getUserMedia microphone capture via useAudioCapture hook (AC-2)
 * - audio_chunk messages sent as base64 PCM to the pipeline (AC-2)
 * - end_turn sent when agent releases the mic button (AC-3)
 * - Incoming audio_chunk (AI PCM) decoded and played via Web Audio API (AC-4)
 * - Mic status indicator: idle / capturing / denied / unsupported
 * - Interruption: audio capture fires immediately while AI is speaking;
 *   the pipeline-side VAD detects the agent speech and emits InterruptionFrame
 *   which cancels the active Polly stream (AC-1 pipeline side)
 *
 * ## Still in scope from prior sprints
 * - WebSocket lifecycle: connect, reconnect, end session
 * - Transcript display (partial + final turns)
 * - Compliance violation banners (dismissible)
 * - Internationalisation via react-i18next namespace `training`
 *
 * ADR references:
 *   adr-001-pipecat-realtime-pipeline.md
 *   adr-003-amazon-transcribe-polly-stt-tts.md
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isWsServerMessage } from '../types';
import type {
  TranscriptTurn,
  WsConnected,
  WsPipelineState,
  WsTranscriptFinal,
  WsTranscriptPartial,
  WsComplianceViolation,
  WsAudioChunk,
} from '../types';
import { useAudioCapture } from '../hooks/useAudioCapture';
import { MicVisualizer } from '../components/MicVisualizer';
import { useAudioPlayback } from '../hooks/useAudioPlayback';
import { CoachingHintsPanel } from '../components/CoachingHintsPanel';
import { getCoachingHints } from '../data/coaching-hints';
import { isEnglish } from '../utils/languageDetection';
import { translateText } from '../utils/translateApi';
import styles from './SessionPage.module.css';

// ---------------------------------------------------------------------------
/** Friendly label for a speech provider id (for the in-session provider badge). */
function providerLabel(id: string): string {
  const map: Record<string, string> = {
    aws_transcribe: 'Amazon Transcribe',
    aws_polly: 'Amazon Polly',
    deepgram: 'Deepgram',
    nova_sonic: 'Amazon Nova Sonic',
    azure_speech: 'Azure Speech',
    elevenlabs: 'ElevenLabs',
  };
  return map[id] ?? id;
}

// Trial Play / AI-Assisted Learning Types & Helper
// ---------------------------------------------------------------------------

interface AssistGuide {
  suggestedScript: string;
  goals: string[];
}

function getTrialGuide(scenarioId: string, aiQuestion: string): AssistGuide {
  const q = aiQuestion.toLowerCase();
  
  // Default fallback guide
  const defaultGuide: AssistGuide = {
    suggestedScript: "I understand your point completely. Let's look at how we can tailor our plan to align with your personal needs and ensure you are comfortable with the terms.",
    goals: [
      "Acknowledge the customer's point and validate their perspective.",
      "Ask an open-ended question to understand their financial or coverage goals.",
      "Explain a simple product benefit without using confusing industry jargon."
    ]
  };

  // Scenario: Life Insurance First Meeting (Taiwanese/English)
  if (
    scenarioId === 'aab4e3d0-1234-5678-abcd-112233445566' || 
    scenarioId === 'ccd6f4e1-3456-7890-cdef-334455667788' || 
    scenarioId.includes('20000000-0000-0000-0000-000000000001') || 
    scenarioId.includes('20000000-0000-0000-0000-000000000003')
  ) {
    if (q.includes('hello') || q.includes('hi') || q.includes('first time') || q.includes('你好') || q.includes('請自我介紹') || q.includes('開始')) {
      return {
        suggestedScript: "Thank you for taking the time to meet with me. Since this is your first time considering life insurance, I want to keep things clear and simple. May I ask what major goals or protection needs you are focusing on right now?",
        goals: [
          "Establish a warm, professional connection.",
          "Set the agenda: focus on needs assessment rather than pushing products.",
          "Ask an open-ended question about their family or financial situation."
        ]
      };
    }
    if (q.includes('expensive') || q.includes('premium') || q.includes('cost') || q.includes('money') || q.includes('貴') || q.includes('保費') || q.includes('預算')) {
      return {
        suggestedScript: "I completely understand that budget is a priority. Life insurance is designed to protect your family from major financial risks. We can start with a basic term plan that fits comfortably within your monthly budget and scale up later.",
        goals: [
          "Empathize with their budget concerns immediately.",
          "Frame the premium as an investment in family protection rather than an expense.",
          "Propose starting with a flexible term insurance policy."
        ]
      };
    }
    if (q.includes('nhi') || q.includes('government') || q.includes('健保') || q.includes('國家')) {
      return {
        suggestedScript: "While National Health Insurance is excellent, private policies cover advanced treatments, private rooms, and lost income during recovery. Private plans guarantee you receive top-tier care without stress.",
        goals: [
          "Validate the strength of public health insurance.",
          "Point out specific gaps: advanced therapy costs, room upgrades, and income replacement.",
          "Position the plan as a complement to existing public coverage."
        ]
      };
    }
  }

  // Scenario: Health Insurance Objection Handling
  if (
    scenarioId === 'bbc5e3d0-2345-6789-bcde-223344556677' || 
    scenarioId.includes('20000000-0000-0000-0000-000000000002')
  ) {
    if (q.includes('already have') || q.includes('enough') || q.includes('已有') || q.includes('夠了')) {
      return {
        suggestedScript: "It's great that you already have coverage in place. However, medical costs and hospital fees have risen significantly. Let's review if your current policy covers critical illness living benefits so you don't have to dip into savings.",
        goals: [
          "Acknowledge and commend their foresight in purchasing insurance.",
          "Highlight rising healthcare inflation and cost gaps.",
          "Propose a comparative check focused on living benefits and critical illness."
        ]
      };
    }
  }

  return defaultGuide;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY_MS = 2000;

/** 16-bit signed PCM at 16 kHz, mono (matches Polly + Transcribe spec) */
const PCM_SAMPLE_RATE = 16_000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// TranscriptTurn is imported from ../types (EPIC-06-04 adds originalText?)

interface ComplianceAlert {
  id: string; // client-assigned for React key
  rule_id: string;
  message: string;
  guidance: string;
  dismissed: boolean;
}

type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SessionPage(): JSX.Element {
  const { t } = useTranslation('training');
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  // Session metadata — read from sessionStorage set by ScenarioSelectPage (#8 / EPIC-06-02)
  const scenarioTitle = sessionStorage.getItem('scenario_title') ?? '';
  const scenarioId = sessionStorage.getItem('scenario_id') ?? '';
  const personaId = sessionStorage.getItem('persona_id') ?? '';
  // AC-6: session language determines whether auto-translation is active
  const sessionLanguage = sessionStorage.getItem('session_language') ?? 'en';
  // Evaluation mode (set on the Scenario screen): 'assessment' is a graded run —
  // the AI-assisted "Trial Play" mode must NOT be offered there (it would let the
  // agent see suggested scripts during a scored session). Trial Play is only for
  // 'coaching'. Defaults to coaching when unset.
  const evaluationMode = sessionStorage.getItem('evaluation_mode') ?? 'coaching';
  const allowTrialPlay = evaluationMode !== 'assessment';

  // Coaching hints — looked up from static fixture by scenario_id (EPIC-06-02)
  const coachingHints = getCoachingHints(scenarioId);

  // Session state
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const [transcriptTurns, setTranscriptTurns] = useState<TranscriptTurn[]>([]);
  const [partialText, setPartialText] = useState<string>('');
  const [partialSpeaker, setPartialSpeaker] = useState<'agent' | 'ai' | null>(null);
  const [complianceAlerts, setComplianceAlerts] = useState<ComplianceAlert[]>([]);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  // Text input — always available (#3); agent can type instead of (or alongside) voice
  const [textInput, setTextInput] = useState<string>('');
  // True when the server echoes back that we are in mock mode (from the "connected" message)
  const [isMockMode, setIsMockMode] = useState<boolean>(false);
  // Pipeline state from the server — used to show AI thinking/speaking indicators (#1)
  const [pipelineState, setPipelineState] = useState<string>('listening');
  // Active speech providers for the in-session badge (#2) — from "audio_config" msg
  const [audioProviders, setAudioProviders] = useState<{ stt: string; tts: string; mode: string; show_badge: boolean } | null>(null);
  // Live objective tracking (EPIC: per-turn judge) — checklist of scenario goals.
  const [objectivesProgress, setObjectivesProgress] = useState<{ objectives: string[]; met: number[] } | null>(null);
  // Coaching hints panel open/close state (EPIC-06-02)
  const [showHints, setShowHints] = useState<boolean>(false);
  const [isTrialMode, setIsTrialMode] = useState<boolean>(false);
  const [trialHint, setTrialHint] = useState<AssistGuide | null>(null);
  const [trialHintLoading, setTrialHintLoading] = useState<boolean>(false);
  const [trialHintError, setTrialHintError] = useState<boolean>(false);

  const lastAiTurn = useMemo(() => {
    const aiTurns = transcriptTurns.filter((t) => t.speaker === 'ai');
    return aiTurns.length > 0 ? aiTurns[aiTurns.length - 1] : null;
  }, [transcriptTurns]);

  const aiQuestionText = lastAiTurn ? lastAiTurn.text : (partialSpeaker === 'ai' ? partialText : '');

  const trialGuide = useMemo(() => {
    return getTrialGuide(scenarioId, aiQuestionText);
  }, [scenarioId, aiQuestionText]);

  // Access token from sessionStorage — declared here (before the trial-hint effect that
  // references it) to avoid a temporal-dead-zone "Cannot access 'accessToken' before
  // initialization" crash.
  const accessToken = sessionStorage.getItem('access_token') ?? '';

  useEffect(() => {
    if (!isTrialMode) {
      setTrialHint(null);
      setTrialHintLoading(false);
      setTrialHintError(false);
      return;
    }

    const question = lastAiTurn?.text?.trim();
    if (!question) {
      setTrialHint(null);
      setTrialHintLoading(false);
      setTrialHintError(false);
      return;
    }

    let cancelled = false;
    setTrialHintLoading(true);
    setTrialHintError(false);

    // Backend route is POST /api/v1/sessions/{id}/trial-hint (TrialHintService,
    // Bedrock Haiku). It returns { data: { goals, suggested_script } }.
    void fetch(`/api/v1/sessions/${sessionId}/trial-hint`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({
        ai_question: question,
        persona_id: personaId,
        language: sessionLanguage,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const payload = await response.json();
        const data = payload?.data ?? payload ?? {};
        const nextHint: AssistGuide = {
          goals: Array.isArray(data.goals) ? data.goals.map((goal: unknown) => String(goal)) : [],
          suggestedScript: String(data.suggested_script ?? data.suggestedScript ?? ''),
        };
        if (!cancelled) {
          setTrialHint(nextHint);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTrialHint(null);
          setTrialHintError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTrialHintLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    isTrialMode,
    lastAiTurn,
    accessToken,
    personaId,
    sessionLanguage,
    sessionId,
  ]);

  const draftFeedback = useMemo(() => {
    if (!isTrialMode) return null;
    const text = textInput.trim().toLowerCase();
    if (text.length < 5) return 'Start typing your response to receive real-time AI guidance.';

    const hintsList: string[] = [];

    // Return guarantees
    if (
      text.includes('guarantee') ||
      text.includes('100%') ||
      text.includes('no risk') ||
      text.includes('保證') ||
      text.includes('一定會賺')
    ) {
      hintsList.push('⚠️ Compliance Alert: Avoid guaranteeing investment returns or capital safety.');
    }

    const q = aiQuestionText.toLowerCase();
    if (
      q.includes('expensive') ||
      q.includes('premium') ||
      q.includes('cost') ||
      q.includes('貴') ||
      q.includes('保費')
    ) {
      if (
        !text.includes('understand') &&
        !text.includes('empathize') &&
        !text.includes('理解') &&
        !text.includes('體諒')
      ) {
        hintsList.push("💡 Tip: Try to first show empathy (e.g., 'I understand that cost is a priority...').");
      }
      if (
        !text.includes('term') &&
        !text.includes('flexible') &&
        !text.includes('定期') &&
        !text.includes('額度')
      ) {
        hintsList.push('💡 Tip: Offer a flexible basic term insurance option or adjust coverage limits to match their budget.');
      }
    }

    if (
      q.includes('already have') ||
      q.includes('enough') ||
      q.includes('已有') ||
      q.includes('夠了')
    ) {
      if (
        !text.includes('inflation') &&
        !text.includes('gap') &&
        !text.includes('review') &&
        !text.includes('評估')
      ) {
        hintsList.push('💡 Tip: Propose a quick coverage gap review or explain the impact of medical cost inflation.');
      }
    }

    if (hintsList.length === 0) {
      return '✨ Draft looks solid! You are addressing the customer nicely. Ready to submit.';
    }

    return hintsList.join('\n');
  }, [isTrialMode, textInput, aiQuestionText]);

  // WebSocket ref — not state, because we never want a re-render when it changes
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const everConnectedRef = useRef(false);   // true once onopen fires; reset on close
  const transcriptBottomRef = useRef<HTMLDivElement>(null);

  // Audio playback hook — gap-free PCM playback via Web Audio API (AC-5 / EPIC-01-06)
  const { primeAudioContext, enqueueChunk, stopPlayback, cancelPlayback, isOutputActive } = useAudioPlayback(PCM_SAMPLE_RATE);

  // Tokens from sessionStorage (written by ScenarioSelectPage)
  // NOTE: accessToken is declared earlier (above the trial-hint effect) to avoid a
  // temporal-dead-zone crash; only wsUrl is declared here.
  const wsUrl = sessionStorage.getItem('ws_url') ?? '';

  // ---------------------------------------------------------------------------
  // Audio capture — useAudioCapture hook (EPIC-01-04 AC-2, AC-3)
  // ---------------------------------------------------------------------------

  /** Send a typed JSON message over the active WebSocket. */
  const sendWsMessage = useCallback((msg: { type: string; data: unknown }): void => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const { isCapturing, micState, startCapture, stopCapture, getMediaStream } = useAudioCapture({
    sendMessage: sendWsMessage,
    isConnected: connectionStatus === 'connected',
    // Suppress residual echo while the AI's voice is playing, but allow the
    // agent to barge in (prevents the speak→mic→STT loop without blocking
    // interruption).
    isOutputActive,
    // Barge-in: cut the AI's audio off immediately so it stops talking when the
    // agent speaks over it (the chained Deepgram TTS sends the whole reply at
    // once, so we must stop it client-side).
    onBargeIn: cancelPlayback,
  });

  // Sync mic denied state for banner (AC-9 / EPIC-01-06)
  useEffect(() => {
    if (micState === 'denied') {
      setMicDenied(true);
    }
  }, [micState]);

  // ---------------------------------------------------------------------------
  // WebSocket lifecycle
  // ---------------------------------------------------------------------------

  const openWebSocket = useCallback((): void => {
    if (!wsUrl || !accessToken || !sessionId) return;

    setConnectionStatus('connecting');

    // Append JWT and optional persona_id to the WebSocket URL.
    // Use `&` if wsUrl already has a `?` (it always does — session_service adds ?language=…).
    const tokenParam = `token=${encodeURIComponent(accessToken)}`;
    const personaParam = personaId ? `persona_id=${encodeURIComponent(personaId)}` : '';
    // Who-starts: if the agent chose "AI starts", tell the pipeline to open.
    const starterParam =
      sessionStorage.getItem('conversation_starter') === 'ai' ? 'starter=ai' : '';
    // Evaluation mode — recorded on the session so the admin log scores only
    // assessment runs (coaching/trial = practice).
    const evalModeParam = `eval_mode=${encodeURIComponent(evaluationMode)}`;
    const separator = wsUrl.includes('?') ? '&' : '?';
    const wsWithParams = [wsUrl + separator + tokenParam, personaParam, starterParam, evalModeParam]
      .filter(Boolean)
      .join('&');
    const ws = new WebSocket(wsWithParams);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnectionStatus('connected');
      reconnectAttemptsRef.current = 0;
      everConnectedRef.current = true;
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data) as unknown;
      } catch {
        // Malformed frame — ignore
        return;
      }

      if (!isWsServerMessage(parsed)) return;

      switch (parsed.type) {
        case 'connected': {
          // Server tells us whether we are in mock mode — used to show/hide the
          // text input fallback for agents who have no real STT available.
          const connMsg = parsed as WsConnected;
          setIsMockMode(connMsg.data?.mock_mode === true);
          break;
        }

        case 'audio_config': {
          const m = parsed as { data: { stt: string; tts: string; mode: string; show_badge: boolean } };
          setAudioProviders(m.data);
          break;
        }

        case 'objectives_progress': {
          const m = parsed as { data: { objectives: unknown; met: unknown } };
          // Guard against a malformed payload (e.g. a JSON string instead of an
          // array) — iterating a string here would render one objective per
          // character. Only accept a real array of strings.
          const objectives = Array.isArray(m.data.objectives)
            ? m.data.objectives.map((o) => String(o))
            : [];
          const met = Array.isArray(m.data.met)
            ? m.data.met.filter((n): n is number => typeof n === 'number')
            : [];
          setObjectivesProgress({ objectives, met });
          break;
        }

        case 'pipeline_state': {
          // Track pipeline state so we can show AI thinking/speaking indicators (#1)
          const stateMsg = parsed as WsPipelineState;
          setPipelineState(stateMsg.data?.state ?? 'listening');
          break;
        }

        case 'pong':
          break;

        case 'transcript_partial': {
          const msg = parsed as WsTranscriptPartial;
          setPartialText(msg.data.text);
          setPartialSpeaker(msg.data.speaker);
          break;
        }

        case 'transcript_final': {
          const msg = parsed as WsTranscriptFinal;
          setTranscriptTurns((prev) => [
            ...prev,
            { turn: msg.data.turn, speaker: msg.data.speaker, text: msg.data.text },
          ]);
          setPartialText('');
          setPartialSpeaker(null);
          break;
        }

        case 'compliance_violation': {
          const msg = parsed as WsComplianceViolation;
          const alert: ComplianceAlert = {
            id: `${Date.now()}-${Math.random()}`,
            rule_id: msg.data.rule_id,
            message: msg.data.message,
            guidance: msg.data.guidance,
            dismissed: false,
          };
          setComplianceAlerts((prev) => [...prev, alert]);
          break;
        }

        case 'audio_chunk': {
          // EPIC-01-06 AC-5: Play incoming AI voice audio (PCM) through speakers.
          // Server shape: { type: "audio_chunk", data: { data: "<base64>", encoding: "pcm", ... } }
          const msg = parsed as WsAudioChunk;
          const b64 = msg.data?.data ?? '';
          if (!b64) break; // empty mock chunk or missing payload — skip

          // enqueueChunk is async — fire-and-forget is intentional here.
          // The AudioContext was primed (resumed) from the mic button user gesture,
          // so this call should play immediately without a suspended-context stall.
          void enqueueChunk(b64);
          break;
        }

        case 'session_ended': {
          // Pipeline auto-end (time limit / all objectives met). The pipeline only
          // closes the stream — scoring is triggered by the /end endpoint, so call
          // it here (mirrors manual End Session) before navigating to the summary.
          sessionStorage.setItem(
            'session_mode', evaluationMode === 'assessment' ? 'assessment' : 'trial',
          );
          void fetch(`/api/v1/sessions/${sessionId}/end`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
          }).catch(() => undefined);
          ws.close(1000);
          navigate(`/session/${sessionId ?? ''}/summary`, { replace: true });
          break;
        }

        case 'error': {
          // Pipeline or STT error — surface in connection status
          setConnectionStatus('disconnected');
          break;
        }

        default:
          break;
      }
    };

    ws.onclose = (event) => {
      wsRef.current = null;
      everConnectedRef.current = false;

      // Code 1000 = intentional clean close (End Session button) — no reconnect
      if (event.code === 1000) return;

      // Unexpected disconnect — attempt reconnect up to MAX_RECONNECT_ATTEMPTS
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        setConnectionStatus('reconnecting');
        setTimeout(openWebSocket, RECONNECT_DELAY_MS);
      } else {
        setConnectionStatus('disconnected');
      }
    };

    ws.onerror = () => {
      // onerror is always followed by onclose — handle there
      setConnectionStatus('reconnecting');
    };
  }, [wsUrl, accessToken, sessionId, navigate]);

  // Open WS on mount; close on unmount.
  // The 50ms delay is intentional: React 18 StrictMode (dev-only) simulates an
  // immediate unmount+remount to detect side-effect leaks. Without the delay,
  // StrictMode's cleanup fires before the WebSocket's onopen, causing a spurious
  // disconnect. The timeout is cancelled by the cleanup, so only the *final*
  // (real) mount actually opens the socket.
  useEffect(() => {
    const timer = setTimeout(() => {
      openWebSocket();
    }, 50);
    return () => {
      clearTimeout(timer);
      wsRef.current?.close(1000);
      everConnectedRef.current = false;
      // Stop audio playback and release AudioContext on unmount (AC-7 / EPIC-01-06)
      stopPlayback();
    };
  }, [openWebSocket, stopPlayback]);

  // Auto-scroll transcript to bottom on new turns
  useEffect(() => {
    transcriptBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcriptTurns, partialText]);

  // ---------------------------------------------------------------------------
  // Text input send — sends typed text as the agent transcript (#3)
  // Works in both mock mode and real mode (pipeline handles text transcript).
  // In real mode this goes through _handle_turn_real directly without STT.
  // ---------------------------------------------------------------------------

  const sendTextTurn = useCallback((): void => {
    const text = textInput.trim();
    if (!text || connectionStatus !== 'connected') return;
    setTextInput('');

    // AC-6: only auto-translate when session is non-English and input looks English
    const shouldTranslate = sessionLanguage !== 'en' && isEnglish(text);

    if (!shouldTranslate) {
      sendWsMessage({ type: 'end_turn', data: { transcript: text } });
      return;
    }

    // AC-3/AC-4: call /api/v1/translate; fall back to original on timeout or error
    void translateText(text, 'en', sessionLanguage, accessToken, 2000).then(
      (translated) => {
        const finalText = translated ?? text; // AC-4: null → use original
        // AC-7: send translated text to pipeline
        sendWsMessage({ type: 'end_turn', data: { transcript: finalText } });
        // AC-2: append to transcript showing both original and translated
        setTranscriptTurns((prev) => [
          ...prev,
          {
            turn: prev.length + 1,
            speaker: 'agent' as const,
            text: finalText,
            ...(translated !== null ? { originalText: text } : {}),
          },
        ]);
      },
    );
  }, [textInput, connectionStatus, sendWsMessage, sessionLanguage, accessToken]);

  // ---------------------------------------------------------------------------
  // End session
  // ---------------------------------------------------------------------------

  const handleEndSession = useCallback(async (): Promise<void> => {
    if (isEnding || !sessionId) return;
    setIsEnding(true);

    // Stop mic and audio playback before ending (AC-7 / EPIC-01-06)
    if (isCapturing) {
      stopCapture();
    }
    stopPlayback();

    try {
      await fetch(`/api/v1/sessions/${sessionId}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch {
      // Best-effort — navigate to summary regardless
    }

    // Record the mode so the summary can present Trial Play as a practice
    // session (not counted toward certification) rather than a failed assessment.
    sessionStorage.setItem('session_mode', isTrialMode ? 'trial' : 'assessment');

    wsRef.current?.close(1000);
    navigate(`/session/${sessionId}/summary`, { replace: true });
  }, [isEnding, sessionId, accessToken, navigate, isCapturing, stopCapture, stopPlayback, isTrialMode]);

  // ---------------------------------------------------------------------------
  // Compliance alert dismissal
  // ---------------------------------------------------------------------------

  function dismissAlert(id: string): void {
    setComplianceAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, dismissed: true } : a)),
    );
  }

  // ---------------------------------------------------------------------------
  // Speaker label
  // ---------------------------------------------------------------------------

  function speakerLabel(speaker: 'agent' | 'ai'): string {
    return speaker === 'agent' ? t('session.transcript.speakerAgent') : t('session.transcript.speakerAI');
  }

  // ---------------------------------------------------------------------------
  // Mic button label / aria-label
  // ---------------------------------------------------------------------------

  function micButtonLabel(): string {
    if (isCapturing) return t('session.mic.stopButton');
    switch (micState) {
      case 'denied':      return t('session.mic.denied');
      case 'unsupported': return t('session.mic.unsupported');
      case 'error':       return t('session.mic.error');
      default:            return t('session.mic.startButton');
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={styles.page}>
      {/* Course header — shows which scenario the agent is practising (#8) */}
      {scenarioTitle && (
        <div className={styles.courseHeader}>
          <span className={styles.courseLabel}>{t('session.courseLabel')}:</span>
          <span className={styles.courseTitle}>{scenarioTitle}</span>
        </div>
      )}

      {/* Mode selection toggle — Trial Play (AI-assisted) is hidden in a graded
          assessment so the agent can't see suggested scripts mid-test. Only
          shown for coaching sessions. */}
      {allowTrialPlay && (
        <div className={styles.modeToggleContainer}>
          <button
            className={`${styles.modeButton} ${!isTrialMode ? styles.modeButtonActive : ''}`}
            onClick={() => {
              setIsTrialMode(false);
              setShowHints(false);
            }}
            type="button"
          >
            🏆 Real Course
          </button>
          <button
            className={`${styles.modeButton} ${isTrialMode ? styles.modeButtonActive : ''}`}
            onClick={() => {
              setIsTrialMode(true);
              setShowHints(true);
            }}
            type="button"
          >
            💡 Trial Play (AI-Assisted)
          </button>
        </div>
      )}

      {/* Active speech-provider badge (#2) — tenant-configurable; hover for detail */}
      {audioProviders?.show_badge && (
        <div
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            margin: '0.4rem 0', padding: '0.25rem 0.6rem', borderRadius: '999px',
            background: '#eef3fb', border: '1px solid #cdddf5', color: '#0055a5',
            fontSize: '0.78rem', fontWeight: 600, cursor: 'help', alignSelf: 'flex-start',
          }}
          title={
            `Active speech engine\n` +
            `Speech-to-Text: ${providerLabel(audioProviders.stt)}\n` +
            `Text-to-Speech: ${providerLabel(audioProviders.tts)}\n` +
            `Mode: ${audioProviders.mode === 's2s' ? 'Speech-to-Speech' : 'Chained (STT → LLM → TTS)'}`
          }
        >
          🎙 {providerLabel(audioProviders.stt)} · 🔊 {providerLabel(audioProviders.tts)}
        </div>
      )}

      {/* Live objective checklist — updates as the AI judge marks goals met.
          Collapsed to a slim progress bar by default (so it doesn't eat into
          the fixed-height transcript area below); hover/focus expands it into
          a floating dropdown of the full checklist without reflowing layout. */}
      {objectivesProgress && objectivesProgress.objectives.length > 0 && (
        <div className={styles.objectivesPanel} tabIndex={0}>
          <div className={styles.objectivesBar}>
            <span className={styles.objectivesBarLabel}>
              🎯 Objectives ({objectivesProgress.met.length}/{objectivesProgress.objectives.length})
            </span>
            <div className={styles.objectivesBarTrack}>
              <div
                className={styles.objectivesBarFill}
                style={{
                  width: `${(objectivesProgress.met.length / objectivesProgress.objectives.length) * 100}%`,
                }}
              />
            </div>
          </div>
          <ul className={styles.objectivesList}>
            {objectivesProgress.objectives.map((obj, i) => {
              const done = objectivesProgress.met.includes(i);
              return (
                <li key={i} className={done ? styles.objDone : styles.objPending}>
                  {done ? '✅' : '⬜'} {obj}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Mic denied banner (AC-9 / EPIC-01-06) */}
      {micDenied && (
        <div className={styles.statusBannerError} role="alert">
          {t('session.mic_denied')}
          <button
            className={styles.alertDismiss}
            onClick={() => setMicDenied(false)}
            aria-label={t('session.dismiss')}
          >
            ×
          </button>
        </div>
      )}

      {/* Connection status banner */}
      {connectionStatus === 'connecting' && (
        <div className={styles.statusBanner} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          {t('session.connecting')}
        </div>
      )}
      {connectionStatus === 'reconnecting' && (
        <div className={styles.statusBannerWarning} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          {t('session.reconnecting')}
        </div>
      )}
      {connectionStatus === 'disconnected' && (
        <div className={styles.statusBannerError} role="alert">
          {t('session.transcript.reconnecting')}
        </div>
      )}

      {/* Compliance violation banners */}
      <div className={styles.alertsContainer} aria-live="assertive">
        {complianceAlerts
          .filter((a) => !a.dismissed)
          .map((alert) => (
            <div key={alert.id} className={styles.complianceAlert} role="alert">
              <div className={styles.alertHeader}>
                <strong>{t('session.complianceAlert')}:</strong> {alert.message}
                <button
                  className={styles.alertDismiss}
                  onClick={() => dismissAlert(alert.id)}
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
              <p className={styles.alertGuidance}>
                <strong>{t('session.complianceGuidance')}:</strong> {alert.guidance}
              </p>
            </div>
          ))}
      </div>

      {/* Coaching hints panel — above the transcript, visible when open (EPIC-06-02 AC-2) */}
      {connectionStatus === 'connected' && !isTrialMode && (
        <CoachingHintsPanel
          hints={coachingHints}
          isOpen={showHints}
        />
      )}

      {/* Trial Play Mode - AI-Assisted Learning Guide */}
      {connectionStatus === 'connected' && isTrialMode && (
        <div className={styles.trialGuideContainer} id="coaching-hints-panel">
          <div className={styles.trialGuideHeader}>
            <span className={styles.trialGuideTitle}>💡 AI-Assisted Learning Guide</span>
            <span className={styles.trialGuideSubtitle}>Trial Play Mode Active</span>
          </div>
          
          <div className={styles.trialGuideBody}>
            {trialHintLoading ? (
              <p className={styles.trialHintLoading}>{t('session.trialHint.loading')}</p>
            ) : trialHintError || !lastAiTurn ? (
              <p className={styles.trialHintLoading}>{t('session.trialHint.placeholder')}</p>
            ) : (
              <>
                <div className={styles.guideSection}>
                  <h4>🎯 Your Key Goals for this response:</h4>
                  <ul>
                    {(trialHint?.goals.length ? trialHint.goals : trialGuide.goals).map((g, idx) => (
                      <li key={idx}>{g}</li>
                    ))}
                  </ul>
                </div>

                <div className={styles.guideSection}>
                  <h4>📝 Suggested Response Script (Recommended):</h4>
                  <div className={styles.scriptBox}>
                    <p>{trialHint?.suggestedScript || trialGuide.suggestedScript}</p>
                    <button
                      className={styles.copyScriptBtn}
                      onClick={() => setTextInput(trialHint?.suggestedScript || trialGuide.suggestedScript)}
                      type="button"
                    >
                      Use This Script
                    </button>
                  </div>
                </div>
              </>
            )}

            {textInput.trim() && (
              <div className={styles.draftFeedbackSection}>
                <h4>🔍 Real-Time Draft Feedback:</h4>
                <div className={styles.feedbackBox}>
                  <pre className={styles.feedbackText}>{draftFeedback}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transcript panel */}
      <div className={styles.transcriptPanel} aria-label="Session transcript" aria-live="polite">
        {transcriptTurns.length === 0 && connectionStatus === 'connected' && (
          <p className={styles.transcriptEmpty}>{t('session.transcript.ready')}</p>
        )}

        {transcriptTurns.map((turn) => (
          <div
            key={`${turn.turn}-${turn.speaker}`}
            className={`${styles.transcriptTurn} ${
              turn.speaker === 'agent' ? styles.turnAgent : styles.turnAI
            }`}
          >
            <span className={styles.speakerLabel}>{speakerLabel(turn.speaker)}</span>
            {/* AC-2: show original English alongside translated text when auto-translated */}
            {turn.originalText !== undefined && (
              <p className={styles.turnTextOriginal}>
                <span className={styles.translatedFromLabel}>
                  {t('session.transcript.translatedFrom')}:
                </span>{' '}
                {turn.originalText}
              </p>
            )}
            <p className={styles.turnText}>{turn.text}</p>
          </div>
        ))}

        {/* Live mic indicator — shown immediately while capturing, before STT partials arrive */}
        {isCapturing && partialText === '' && (
          <div
            className={`${styles.transcriptTurn} ${styles.partial} ${styles.turnAgent}`}
            aria-label={t('session.transcript.inProgress')}
          >
            <span className={styles.speakerLabel}>{speakerLabel('agent')}</span>
            <p className={styles.turnText}>
              <span className={styles.listeningDots} aria-hidden="true">●&nbsp;●&nbsp;●</span>
            </p>
          </div>
        )}

        {/* Partial (in-progress) turn — streaming text from STT/LLM */}
        {partialText !== '' && partialSpeaker !== null && (
          <div
            className={`${styles.transcriptTurn} ${styles.partial} ${
              partialSpeaker === 'agent' ? styles.turnAgent : styles.turnAI
            }`}
            aria-label={t('session.transcript.inProgress')}
          >
            <span className={styles.speakerLabel}>{speakerLabel(partialSpeaker)}</span>
            <p className={styles.turnText}>{partialText}</p>
          </div>
        )}

        {/* AI thinking indicator — shown while pipeline_state is 'thinking' (#1) */}
        {pipelineState === 'thinking' && partialText === '' && !isCapturing && (
          <div
            className={`${styles.transcriptTurn} ${styles.partial} ${styles.turnAI}`}
            aria-label={t('session.aiThinking')}
            aria-live="polite"
          >
            <span className={styles.speakerLabel}>{speakerLabel('ai')}</span>
            <p className={styles.turnText}>
              <span className={styles.listeningDots} aria-hidden="true">●&nbsp;●&nbsp;●</span>
            </p>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={transcriptBottomRef} />
      </div>

      {/* Live mic visualizer — confirms the app is capturing the agent's voice.
          While the AI is speaking the mic is gated (half-duplex), so reflect
          that rather than showing a misleading "Listening…". */}
      <MicVisualizer
        active={isCapturing}
        getStream={getMediaStream}
        label={
          pipelineState === 'speaking'
            ? t('session.mic.aiSpeaking', { defaultValue: 'AI speaking — mic paused' })
            : t('session.mic.listening', { defaultValue: 'Listening…' })
        }
      />

      {/* Bottom controls: mic button + end session */}
      <div className={styles.controls}>
        {/* Text input — always available (#3). Agent can type instead of or
            alongside voice. In real mode the typed transcript goes directly to
            the LLM via end_turn.data.transcript; in mock mode it drives the
            scripted response flow. Press Enter or click Send to submit. */}
        <div className={styles.mockInputRow}>
          <input
            type="text"
            className={styles.mockInput}
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') sendTextTurn(); }}
            placeholder={
              isMockMode
                ? t('session.mockInputPlaceholder')
                : t('session.textInputPlaceholder')
            }
            disabled={connectionStatus !== 'connected' || isEnding}
            aria-label={t('session.textInputPlaceholder')}
          />
          <button
            className={styles.mockSendButton}
            onClick={sendTextTurn}
            disabled={!textInput.trim() || connectionStatus !== 'connected' || isEnding}
          >
            {t('session.textInputSend')}
          </button>
        </div>

        {/* Mic button — EPIC-01-04 AC-2/AC-3 */}
        {isCapturing ? (
          <button
            className={`${styles.micButton} ${styles.micButtonActive}`}
            onClick={() => {
              if (connectionStatus !== 'connected') return;
              stopCapture();
            }}
            disabled={
              isEnding ||
              connectionStatus !== 'connected' ||
              micState === 'denied' ||
              micState === 'unsupported'
            }
            aria-label={micButtonLabel()}
            aria-pressed="true"
          >
            <span
              className={`${styles.micIcon} ${styles.micIconCapturing}`}
              aria-hidden="true"
            >
              🔴
            </span>
            <span className={styles.micLabel}>{micButtonLabel()}</span>
          </button>
        ) : (
          <button
            className={`${styles.micButton} ${
              micState === 'denied' || micState === 'unsupported' ? styles.micButtonDisabled : ''
            }`}
            onClick={() => {
              if (connectionStatus !== 'connected') return;
              void primeAudioContext().then(() => startCapture());
            }}
            disabled={
              isEnding ||
              connectionStatus !== 'connected' ||
              micState === 'denied' ||
              micState === 'unsupported'
            }
            aria-label={micButtonLabel()}
            aria-pressed="false"
          >
            <span
              className={styles.micIcon}
              aria-hidden="true"
            >
              🎙️
            </span>
            <span className={styles.micLabel}>{micButtonLabel()}</span>
          </button>
        )}

        {/* Mic state hint for denied / unsupported */}
        {(micState === 'denied' || micState === 'unsupported') && (
          <p className={styles.micHint} role="alert">
            {micState === 'denied'
              ? t('session.mic.deniedHint')
              : t('session.mic.unsupportedHint')}
          </p>
        )}

        {/* Hints toggle button — AC-1 (EPIC-06-02): visible only when connected (AC-7) */}
        {connectionStatus === 'connected' && (
          showHints ? (
            <button
              className={`${styles.hintsButton} ${styles.hintsButtonActive}`}
              onClick={() => setShowHints(false)}
              aria-pressed="true"
              aria-expanded="true"
              aria-controls="coaching-hints-panel"
              data-testid="hints-toggle-button"
            >
              {t('session.hints.toggleButtonClose')}
            </button>
          ) : (
            <button
              className={styles.hintsButton}
              onClick={() => setShowHints(true)}
              aria-pressed="false"
              aria-expanded="false"
              aria-controls="coaching-hints-panel"
              data-testid="hints-toggle-button"
            >
              {t('session.hints.toggleButton')}
            </button>
          )
        )}

        <button
          className={styles.endButton}
          onClick={() => setShowEndConfirm(true)}
          disabled={isEnding || connectionStatus === 'connecting'}
        >
          {t('session.endSession')}
        </button>
      </div>

      {/* End session confirmation modal */}
      {showEndConfirm && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <p className={styles.modalMessage}>{t('session.endSessionConfirm')}</p>
            <div className={styles.modalActions}>
              {isEnding ? (
                <button
                  className={styles.btnPrimary}
                  onClick={() => void handleEndSession()}
                  disabled
                  aria-busy="true"
                >
                  {t('session.endSessionConfirmButton')}
                </button>
              ) : (
                <button
                  className={styles.btnPrimary}
                  onClick={() => void handleEndSession()}
                  aria-busy="false"
                >
                  {t('session.endSessionConfirmButton')}
                </button>
              )}
              <button
                className={styles.btnSecondary}
                onClick={() => setShowEndConfirm(false)}
                disabled={isEnding}
              >
                {t('session.endSessionCancelButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
